import type { AssistantReportDefinition } from "../entities/assistant";
import type {
  RunReportsEntityRow,
  RunReportsPrimitiveCell,
} from "./runReportsPreview";

export const YSTNJ_SCHOLARSHIP_WORKSHEET_TEMPLATE_ID =
  "ystnj-scholarship-worksheet";
export const YSTNJ_SCHOLARSHIP_WORKSHEET_ENTITY_ID =
  "scholarship_worksheet_rows";
export const YSTNJ_SYNTHETIC_FINANCIAL_AID_FAMILY_ID_PREFIX =
  "financial-aid-applicant:";

export const YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS = {
  lastName: "scholarship_worksheet_last_name",
  childrenPre1To8: "scholarship_worksheet_children_pre_1_8",
  childrenNurseryKindergarten: "scholarship_worksheet_children_n_k",
  contractAmount: "scholarship_worksheet_contract_amount",
  requestedAmount: "scholarship_worksheet_requested_amount",
  requestedPercent: "scholarship_worksheet_requested_pct",
  revisedContract: "scholarship_worksheet_revised_contract",
  awardAmount: "scholarship_worksheet_award_amount",
  awardPercent: "scholarship_worksheet_award_pct",
  memo: "scholarship_worksheet_memo",
} as const;

export const YSTNJ_SCHOLARSHIP_WORKSHEET_COLUMNS = [
  {
    fieldId: YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.lastName,
    header: "Last Name",
    kind: "text",
    width: 18,
  },
  {
    fieldId: YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.childrenPre1To8,
    header: "Students in Pre-1 through Grade 8",
    kind: "integer",
    width: 28,
  },
  {
    fieldId: YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.childrenNurseryKindergarten,
    header: "Students in Nursery through Kindergarten",
    kind: "integer",
    width: 32,
  },
  {
    fieldId: YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.contractAmount,
    header: "Contract Amount",
    kind: "currency",
    width: 16,
  },
  {
    fieldId: YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.requestedAmount,
    header: "Scholarship Requested",
    kind: "currency",
    width: 20,
  },
  {
    fieldId: YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.requestedPercent,
    header: "Scholarship % Requested",
    kind: "percentage",
    width: 24,
  },
  {
    fieldId: YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.revisedContract,
    header: "Revised Contract",
    kind: "currency",
    width: 16,
  },
  {
    fieldId: YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.awardAmount,
    header: "Scholarship Amount",
    kind: "currency",
    width: 18,
  },
  {
    fieldId: YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.awardPercent,
    header: "Scholarship % Given",
    kind: "percentage",
    width: 18,
  },
  {
    fieldId: YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.memo,
    header: "Memo",
    kind: "text",
    width: 28,
  },
] as const;

export type YstnjScholarshipWorksheetFieldId =
  (typeof YSTNJ_SCHOLARSHIP_WORKSHEET_COLUMNS)[number]["fieldId"];

export type YstnjScholarshipWorksheetColumnKind =
  (typeof YSTNJ_SCHOLARSHIP_WORKSHEET_COLUMNS)[number]["kind"];

export const isYstnjScholarshipWorksheetDefinition = (
  definition:
    | Pick<
        AssistantReportDefinition,
        "templateId" | "selectedEntities" | "selectedFields"
      >
    | null
    | undefined,
): boolean => {
  if (
    definition?.selectedEntities?.length !== 1 ||
    definition.selectedEntities[0] !== YSTNJ_SCHOLARSHIP_WORKSHEET_ENTITY_ID
  ) {
    return false;
  }
  const worksheetFieldIds = new Set<string>(
    YSTNJ_SCHOLARSHIP_WORKSHEET_COLUMNS.map((column) => column.fieldId),
  );
  return (
    definition.selectedFields.length === worksheetFieldIds.size &&
    definition.selectedFields.every((fieldId) => worksheetFieldIds.has(fieldId))
  );
};

const toText = (value: unknown): string | null => {
  if (typeof value !== "string" && typeof value !== "number") {
    return null;
  }
  const normalized = String(value).trim();
  return normalized.length > 0 ? normalized : null;
};

const toNumber = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const parsed = Number(value.replace(/[$,\s]/g, ""));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const firstNumber = (...values: unknown[]): number | null => {
  for (const value of values) {
    const numeric = toNumber(value);
    if (numeric != null) {
      return numeric;
    }
  }
  return null;
};

const firstPositiveNumber = (...values: unknown[]): number | null => {
  for (const value of values) {
    const numeric = toNumber(value);
    if (numeric != null && numeric > 0) {
      return numeric;
    }
  }
  return null;
};

const ratioOrNull = (
  numerator: number | null,
  denominator: number | null,
): number | null =>
  numerator != null && denominator != null && denominator > 0
    ? numerator / denominator
    : null;

const normalizeGradeToken = (value: unknown): string =>
  toText(value)
    ?.toLowerCase()
    .replace(/[_]+/g, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim() ?? "";

const isNurseryKindergartenGrade = (value: unknown): boolean => {
  const normalized = normalizeGradeToken(value);
  if (!normalized) {
    return false;
  }
  return (
    /\b(n|nursery|toddler|playgroup|pre k|prek|prekindergarten|kindergarten|k)\b/.test(
      normalized,
    ) || normalized === "pk"
  );
};

const isPre1To8Grade = (value: unknown): boolean => {
  const normalized = normalizeGradeToken(value);
  if (!normalized || isNurseryKindergartenGrade(normalized)) {
    return false;
  }
  if (/\bpre\s*1\b/.test(normalized)) {
    return true;
  }
  const match = normalized.match(/\b(?:grade\s*)?([1-8])(?:st|nd|rd|th)?\b/);
  return Boolean(match);
};

const normalizeFamilyLastName = (familyName: unknown): string | null => {
  const normalized = toText(familyName);
  if (!normalized) {
    return null;
  }
  const withoutFamilySuffix = normalized.replace(/\s+family$/i, "").trim();
  const tokens = withoutFamilySuffix.split(/\s+/).filter(Boolean);
  return tokens[tokens.length - 1] ?? withoutFamilySuffix;
};

const escapeSpreadsheetText = (value: string): string =>
  /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;

const rowsForEntity = (
  rowsByEntity: ReadonlyMap<string, readonly RunReportsEntityRow[]>,
  entityId: string,
): readonly RunReportsEntityRow[] => rowsByEntity.get(entityId) ?? [];

const groupRowsByFamilyId = (
  rows: readonly RunReportsEntityRow[],
): Map<string, RunReportsEntityRow[]> => {
  const byFamilyId = new Map<string, RunReportsEntityRow[]>();
  for (const row of rows) {
    const familyId = toText(row.joinKeys.family_id);
    if (!familyId) {
      continue;
    }
    const familyRows = byFamilyId.get(familyId) ?? [];
    familyRows.push(row);
    byFamilyId.set(familyId, familyRows);
  }
  return byFamilyId;
};

const rowsByFamilyId = (
  rowsByEntity: ReadonlyMap<string, readonly RunReportsEntityRow[]>,
  entityId: string,
): Map<string, RunReportsEntityRow[]> =>
  groupRowsByFamilyId(rowsForEntity(rowsByEntity, entityId));

const NON_REPORTABLE_FINANCIAL_AID_STATUSES = new Set([
  "invited",
  "opened",
  "started",
  "draft",
  "closed",
]);

const isReportableFinancialAidRow = (row: RunReportsEntityRow): boolean => {
  const status = toText(row.cells.fa_status)?.toLowerCase() ?? "";
  return !NON_REPORTABLE_FINANCIAL_AID_STATUSES.has(status);
};

const normalizeNameToken = (value: unknown): string =>
  (toText(value) ?? "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

const isLegacyStudentPlaceholder = (row: RunReportsEntityRow): boolean =>
  !toText(row.joinKeys.student_id) &&
  normalizeNameToken(row.cells.first_name) === "student" &&
  normalizeGradeToken(row.cells.grade_level) === "grade 1";

const countStudentBandsFromRows = (
  studentRows: readonly RunReportsEntityRow[],
): {
  childrenPre1To8: number | null;
  childrenNurseryKindergarten: number | null;
} => {
  const uniqueRows = new Map<string, RunReportsEntityRow>();
  const seenIdentityKeys = new Set<string>();
  for (const row of studentRows.filter(
    (candidate) => !isLegacyStudentPlaceholder(candidate),
  )) {
    const studentId = toText(row.joinKeys.student_id);
    const firstName = normalizeNameToken(row.cells.first_name);
    const lastName = normalizeNameToken(row.cells.last_name);
    const profileKey =
      firstName || lastName ? `profile:${firstName}:${lastName}` : null;
    const identityKeys = [
      studentId ? `student:${studentId}` : null,
      profileKey,
    ].filter((key): key is string => Boolean(key));
    if (identityKeys.some((key) => seenIdentityKeys.has(key))) {
      continue;
    }
    const studentKey = identityKeys[0] ?? `row:${row.id}`;
    uniqueRows.set(studentKey, row);
    identityKeys.forEach((key) => seenIdentityKeys.add(key));
  }
  const deduplicatedRows = [...uniqueRows.values()];
  if (deduplicatedRows.length === 0) {
    return {
      childrenPre1To8: null,
      childrenNurseryKindergarten: null,
    };
  }
  return {
    childrenPre1To8: deduplicatedRows.filter((row) =>
      isPre1To8Grade(row.cells.grade_level),
    ).length,
    childrenNurseryKindergarten: deduplicatedRows.filter((row) =>
      isNurseryKindergartenGrade(row.cells.grade_level),
    ).length,
  };
};

const countStudentBands = (input: {
  financialAidRows: readonly RunReportsEntityRow[];
  studentRows: readonly RunReportsEntityRow[];
  scholarshipRows: readonly RunReportsEntityRow[];
}): {
  childrenPre1To8: number | null;
  childrenNurseryKindergarten: number | null;
} => {
  const declaredTotal = firstNumber(
    ...input.financialAidRows.map(
      (row) => row.cells.scholarship_total_children_source,
    ),
  );
  const declaredPreschool = firstNumber(
    ...input.financialAidRows.map(
      (row) => row.cells.scholarship_preschool_children_source,
    ),
  );
  if (
    declaredTotal != null &&
    declaredTotal >= 0 &&
    declaredPreschool != null
  ) {
    const total = Math.floor(declaredTotal);
    const preschool = Math.min(
      total,
      Math.max(0, Math.floor(declaredPreschool)),
    );
    return {
      childrenPre1To8: total - preschool,
      childrenNurseryKindergarten: preschool,
    };
  }
  return countStudentBandsFromRows([
    ...input.studentRows,
    ...input.scholarshipRows,
  ]);
};

const firstCellFromRows = (
  rows: readonly RunReportsEntityRow[],
  fieldId: string,
): RunReportsPrimitiveCell => {
  for (const row of rows) {
    const value = row.cells[fieldId];
    if (value != null && value !== "") {
      return value;
    }
  }
  return null;
};

const selectLatestFinancialAidRow = (
  rows: readonly RunReportsEntityRow[],
): RunReportsEntityRow | null => {
  const sorted = [...rows].sort((left, right) => {
    const leftDate = toText(left.cells.fa_submission_date) ?? "";
    const rightDate = toText(right.cells.fa_submission_date) ?? "";
    const leftTime = Date.parse(leftDate);
    const rightTime = Date.parse(rightDate);
    if (
      Number.isFinite(leftTime) &&
      Number.isFinite(rightTime) &&
      leftTime !== rightTime
    ) {
      return rightTime - leftTime;
    }
    if (leftDate !== rightDate) {
      return rightDate.localeCompare(leftDate);
    }
    return right.id.localeCompare(left.id);
  });
  return sorted[0] ?? null;
};

const applicationIdForRow = (
  row: RunReportsEntityRow | null | undefined,
): string | null =>
  toText(row?.joinKeys.application_id) ?? toText(row?.cells.application_id);

const contractAmountForFamily = (input: {
  tuitionAccountRows: readonly RunReportsEntityRow[];
  contractRows: readonly RunReportsEntityRow[];
  financialAidRows: readonly RunReportsEntityRow[];
}): number | null =>
  firstPositiveNumber(
    ...input.financialAidRows.map(
      (row) => row.cells.scholarship_contract_amount_source,
    ),
    ...input.contractRows.map((row) => row.cells.contract_amount),
    ...input.tuitionAccountRows.map((row) => row.cells.annual_tuition),
  );

const requestedAmountForApplication = (
  financialAidRows: readonly RunReportsEntityRow[],
): number | null =>
  firstPositiveNumber(
    ...financialAidRows.map(
      (row) => row.cells.scholarship_requested_amount_source,
    ),
  );

const awardAmountForApplication = (
  financialAidRows: readonly RunReportsEntityRow[],
  scholarshipRows: readonly RunReportsEntityRow[],
): number | null =>
  firstNumber(
    ...financialAidRows.map((row) => row.cells.scholarship_award_amount_source),
    ...scholarshipRows.map((row) => row.cells.award_amount),
  );

export const buildYstnjScholarshipWorksheetRows = (input: {
  rowsByEntity: ReadonlyMap<string, readonly RunReportsEntityRow[]>;
}): RunReportsEntityRow[] => {
  const familyRowsByFamilyId = rowsByFamilyId(input.rowsByEntity, "families");
  const studentRowsByFamilyId = rowsByFamilyId(input.rowsByEntity, "students");
  const tuitionAccountRowsByFamilyId = rowsByFamilyId(
    input.rowsByEntity,
    "tuition_accounts",
  );
  const contractRowsByFamilyId = rowsByFamilyId(
    input.rowsByEntity,
    "contracts",
  );
  const financialAidRowsByFamilyId = groupRowsByFamilyId(
    rowsForEntity(input.rowsByEntity, "fa_applications").filter(
      isReportableFinancialAidRow,
    ),
  );
  const scholarshipRowsByFamilyId = rowsByFamilyId(
    input.rowsByEntity,
    "scholarships",
  );

  return [...financialAidRowsByFamilyId.entries()]
    .map(([familyId, financialAidRows]) => {
      const selectedFinancialAidRow =
        selectLatestFinancialAidRow(financialAidRows);
      const selectedFinancialAidRows = selectedFinancialAidRow
        ? [selectedFinancialAidRow]
        : [];
      const selectedApplicationId = applicationIdForRow(
        selectedFinancialAidRow,
      );
      const familyRows = familyRowsByFamilyId.get(familyId) ?? [];
      const studentRows = studentRowsByFamilyId.get(familyId) ?? [];
      const tuitionAccountRows =
        tuitionAccountRowsByFamilyId.get(familyId) ?? [];
      const contractRows = contractRowsByFamilyId.get(familyId) ?? [];
      const scholarshipRows = (
        scholarshipRowsByFamilyId.get(familyId) ?? []
      ).filter(
        (row) =>
          selectedApplicationId != null &&
          applicationIdForRow(row) === selectedApplicationId,
      );
      const contractAmount = contractAmountForFamily({
        tuitionAccountRows,
        contractRows,
        financialAidRows: selectedFinancialAidRows,
      });
      const requestedAmount = requestedAmountForApplication(
        selectedFinancialAidRows,
      );
      const awardAmount = awardAmountForApplication(
        selectedFinancialAidRows,
        scholarshipRows,
      );
      const counts = countStudentBands({
        financialAidRows: selectedFinancialAidRows,
        studentRows,
        scholarshipRows,
      });
      const revisedContract =
        contractAmount != null && awardAmount != null
          ? Math.max(contractAmount - awardAmount, 0)
          : null;
      const safeFamilyIdFallback = familyId.startsWith(
        YSTNJ_SYNTHETIC_FINANCIAL_AID_FAMILY_ID_PREFIX,
      )
        ? "Unlinked applicant"
        : familyId;
      const lastName = escapeSpreadsheetText(
        normalizeFamilyLastName(firstCellFromRows(familyRows, "family_name")) ??
          normalizeFamilyLastName(
            firstCellFromRows(
              selectedFinancialAidRows,
              "scholarship_family_name_source",
            ),
          ) ??
          toText(firstCellFromRows(scholarshipRows, "last_name")) ??
          toText(
            firstCellFromRows(selectedFinancialAidRows, "fa_ref_number"),
          ) ??
          safeFamilyIdFallback,
      );
      return {
        id: `scholarship-worksheet:${familyId}`,
        entityId: YSTNJ_SCHOLARSHIP_WORKSHEET_ENTITY_ID,
        joinKeys: {
          family_id: familyId,
          applicant_id:
            toText(selectedFinancialAidRow?.joinKeys.applicant_id) ??
            toText(firstCellFromRows(selectedFinancialAidRows, "applicant_id")),
        },
        cells: {
          [YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.lastName]: lastName,
          [YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.childrenPre1To8]:
            counts.childrenPre1To8,
          [YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.childrenNurseryKindergarten]:
            counts.childrenNurseryKindergarten,
          [YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.contractAmount]:
            contractAmount,
          [YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.requestedAmount]:
            requestedAmount,
          [YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.requestedPercent]: ratioOrNull(
            requestedAmount,
            contractAmount,
          ),
          [YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.revisedContract]:
            revisedContract,
          [YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.awardAmount]: awardAmount,
          [YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.awardPercent]: ratioOrNull(
            awardAmount,
            contractAmount,
          ),
          [YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.memo]: null,
        },
      } satisfies RunReportsEntityRow;
    })
    .sort((left, right) =>
      String(
        left.cells[YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.lastName] ?? "",
      ).localeCompare(
        String(
          right.cells[YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.lastName] ?? "",
        ),
        undefined,
        { sensitivity: "base", numeric: true },
      ),
    );
};

export const buildYstnjScholarshipWorksheetFormulaRows = (input: {
  rows: readonly { cells: Record<string, RunReportsPrimitiveCell> }[];
  dataStartRowNumber: number;
}): Array<Record<string, RunReportsPrimitiveCell>> =>
  input.rows.map((row, index) => {
    const excelRow = input.dataStartRowNumber + index;
    return {
      ...row.cells,
      [YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.requestedPercent]: `=IFERROR(E${excelRow}/D${excelRow},"")`,
      [YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.awardPercent]: `=IFERROR(H${excelRow}/D${excelRow},"")`,
    };
  });

export const buildYstnjScholarshipWorksheetSummaryValues = (input: {
  rowCount: number;
  dataStartRowNumber: number;
}): Record<string, RunReportsPrimitiveCell> | undefined => {
  if (input.rowCount <= 0) {
    return undefined;
  }
  const dataStart = input.dataStartRowNumber;
  const dataEnd = dataStart + input.rowCount - 1;
  const summaryRow = dataEnd + 2;
  return {
    [YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.childrenPre1To8]: `=SUM(B${dataStart}:B${dataEnd})`,
    [YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.childrenNurseryKindergarten]: `=SUM(C${dataStart}:C${dataEnd})`,
    [YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.contractAmount]: `=SUM(D${dataStart}:D${dataEnd})`,
    [YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.requestedAmount]: `=SUM(E${dataStart}:E${dataEnd})`,
    [YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.requestedPercent]: `=IFERROR(E${summaryRow}/D${summaryRow},"")`,
    [YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.revisedContract]: `=SUM(G${dataStart}:G${dataEnd})`,
    [YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.awardAmount]: `=SUM(H${dataStart}:H${dataEnd})`,
    [YSTNJ_SCHOLARSHIP_WORKSHEET_FIELD_IDS.awardPercent]: `=IFERROR(H${summaryRow}/D${summaryRow},"")`,
  };
};

export const buildYstnjScholarshipWorksheetSummaryLabel = (input: {
  rowCount: number;
  dataStartRowNumber: number;
}): string => {
  const dataStart = input.dataStartRowNumber;
  const dataEnd = dataStart + input.rowCount - 1;
  return `=COUNTA(A${dataStart}:A${dataEnd})`;
};
